import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../../../core';
import RestaurantSelectionWrapper from '../../../../modules/restaurant/pages/RestaurantSelection/RestaurantSelectionWrapper';
import Opening from '../../../../modules/shared/pages/opening';
import { authService } from '../../../../modules/shared/api/authService';
import LoadingScreen from '../../../../modules/shared/components/LoadingScreen';
import { isCustomerAppOrigin } from '../../../../modules/shared/utils/subdomainRouter';
import { redirectLoggedInCustomer } from '../../../../modules/shared/utils/authLockUtils';
import FixedRestaurantGate from '../../FixedRestaurantGate';
import '../../inverted-theme.css';
import {
  RESTAURANT_ID as ARIKA_RAM_NAGARI_RESTAURANT_ID,
  DEFAULT_TABLE,
} from './restaurant.config';

function OpeningOrRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const pathname = (location.pathname || '/').replace(/\/$/, '') || '/';
  const search = location.search || '';

  const isCustomerOrigin = () => {
    if (typeof window === 'undefined') return true;
    return isCustomerAppOrigin();
  };

  React.useEffect(() => {
    if (!authLoading && user && pathname === '/' && isCustomerOrigin()) {
      const checkRoleAndRedirect = async () => {
        try {
          await authService.getProfile(user.id);
          redirectLoggedInCustomer(search);
        } catch (err) {
          console.error('Role check failed:', err);
          redirectLoggedInCustomer(search || '');
        }
      };
      checkRoleAndRedirect();
    }
  }, [user, authLoading, pathname, navigate, search]);

  if (pathname !== '/') return <Opening />;
  if (!isCustomerOrigin()) return <Opening />;
  if (authLoading || (user && pathname === '/')) return <Opening />;

  return <Opening />;
}

const OnboardingScreen = lazy(() => import('../../../../modules/shared/pages/onboardingScreen'));
const LoginPage = lazy(() => import('../../../../modules/shared/components/auth/LoginPage'));
const SignupPage = lazy(() => import('../../../../modules/shared/components/auth/SignupPage'));
const CompleteProfile = lazy(
  () => import('../../../../modules/shared/components/auth/CompleteProfile'),
);
const ForgotPassword = lazy(
  () => import('../../../../modules/shared/components/auth/ForgotPassward'),
);
const ResetPassword = lazy(
  () => import('../../../../modules/shared/components/auth/ResetPassword'),
);
const AuthCallback = lazy(() => import('../../../../modules/shared/components/auth/AuthCallback'));

const Homepage = lazy(() => import('../../../../modules/customer/pages/HomePage/Homepage'));

const LockedRestaurantSelection = () => (
  <FixedRestaurantGate restaurantId={ARIKA_RAM_NAGARI_RESTAURANT_ID} defaultTable={DEFAULT_TABLE} />
);
const BestSellerPage = lazy(() => import('../../../../modules/customer/pages/BestSellerPage'));
const LikedItemsPage = lazy(() => import('../../../../modules/customer/pages/LikedItemsPage'));
const MyOrders = lazy(() => import('../../../../modules/customer/pages/MyOrders'));
const OrderConfirmation = lazy(
  () => import('../../../../modules/customer/pages/OrderConfirmation'),
);
const PaymentPage = lazy(() => import('../../../../modules/customer/pages/PaymentPage'));
const ProfilePage = lazy(() => import('../../../../modules/customer/pages/ProfilePage'));
const OrderTracking = lazy(() => import('../../../../modules/customer/pages/OrderTracking'));
const OrderConfirmationPage = lazy(
  () => import('../../../../modules/customer/pages/OrderConfirmationPage'),
);
const ContactUsPage = lazy(() => import('../../../../modules/customer/pages/ContactUsPage'));
const HelpFAQPage = lazy(() => import('../../../../modules/customer/pages/HelpFAQPage'));
const TermsOfService = lazy(
  () => import('../../../../modules/shared/components/legal/TermsOfService'),
);
const PrivacyPolicy = lazy(
  () => import('../../../../modules/shared/components/legal/PrivacyPolicy'),
);
const RequireAuth = lazy(() => import('../../../../modules/shared/components/auth/RequireAuth'));
const NotFoundPage = lazy(() => import('../../../../modules/shared/components/NotFoundPage'));
const LoadingSpinner = () => <LoadingScreen message="Loading..." />;

const AUTH_PATHS = [
  '/login',
  '/signup',
  '/complete-profile',
  '/forgot-password',
  '/reset-password',
  '/auth-callback',
  '/restaurant-selection',
  '/onboarding',
  '/terms',
  '/privacy',
];

const FallbackRedirect = ({ NotFound }) => {
  const location = useLocation();
  const pathname = location.pathname.replace(/\/$/, '') || '/';
  const isAuthPath = AUTH_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'));
  if (isAuthPath) {
    if (pathname === '/login' || pathname.startsWith('/login')) return <LoginPage />;
    if (pathname === '/signup' || pathname.startsWith('/signup')) return <SignupPage />;
    if (pathname === '/onboarding' || pathname.startsWith('/onboarding'))
      return <OnboardingScreen />;
    if (pathname === '/complete-profile' || pathname.startsWith('/complete-profile'))
      return <CompleteProfile />;
    if (pathname === '/forgot-password' || pathname.startsWith('/forgot-password'))
      return <ForgotPassword />;
    if (pathname === '/reset-password' || pathname.startsWith('/reset-password'))
      return <ResetPassword />;
    if (pathname === '/auth-callback' || pathname.startsWith('/auth-callback'))
      return <AuthCallback />;
    if (pathname === '/restaurant-selection' || pathname.startsWith('/restaurant-selection'))
      return <LockedRestaurantSelection />;
    if (pathname === '/terms' || pathname.startsWith('/terms')) return <TermsOfService />;
    if (pathname === '/privacy' || pathname.startsWith('/privacy')) return <PrivacyPolicy />;
    return <Navigate to={pathname} replace />;
  }
  if (NotFound) return <NotFound />;
  return <Navigate to="/" replace />;
};

/**
 * Arika's Ram Nagari — inverted color scheme (light shell, dark content card).
 * Restaurant switching is disabled — guests stay on this outlet only.
 */
const ArikasRamNagariInvertedCustomerApp = () => (
  <div
    data-restaurant-theme={ARIKA_RAM_NAGARI_RESTAURANT_ID}
    data-theme-slug="arikas-ram-nagari-inverted"
  >
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        <Route index element={<OpeningOrRedirect />} />
        <Route path="/onboarding" element={<OnboardingScreen />} />
        <Route path="/login/*" element={<LoginPage />} />
        <Route path="/signup/*" element={<SignupPage />} />
        <Route path="/complete-profile/*" element={<CompleteProfile />} />
        <Route path="/forgot-password/*" element={<ForgotPassword />} />
        <Route path="/reset-password/*" element={<ResetPassword />} />
        <Route path="/auth-callback/*" element={<AuthCallback />} />
        <Route path="/restaurant-selection" element={<LockedRestaurantSelection />} />
        <Route path="/restaurant-selection/*" element={<LockedRestaurantSelection />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />

        <Route
          path="/home"
          element={
            <RestaurantSelectionWrapper>
              <Homepage />
            </RestaurantSelectionWrapper>
          }
        />
        <Route
          path="/best-sellers"
          element={
            <RestaurantSelectionWrapper>
              <BestSellerPage />
            </RestaurantSelectionWrapper>
          }
        />
        <Route
          path="/liked-items"
          element={
            <RestaurantSelectionWrapper>
              <LikedItemsPage />
            </RestaurantSelectionWrapper>
          }
        />
        <Route
          path="/my-orders"
          element={
            <RequireAuth>
              <RestaurantSelectionWrapper>
                <MyOrders />
              </RestaurantSelectionWrapper>
            </RequireAuth>
          }
        />
        <Route
          path="/order-confirmation"
          element={
            <RestaurantSelectionWrapper>
              <OrderConfirmation />
            </RestaurantSelectionWrapper>
          }
        />
        <Route
          path="/payment"
          element={
            <RestaurantSelectionWrapper>
              <PaymentPage />
            </RestaurantSelectionWrapper>
          }
        />
        <Route
          path="/profile"
          element={
            <RequireAuth>
              <RestaurantSelectionWrapper>
                <ProfilePage />
              </RestaurantSelectionWrapper>
            </RequireAuth>
          }
        />
        <Route
          path="/order-tracking"
          element={
            <RestaurantSelectionWrapper>
              <OrderTracking />
            </RestaurantSelectionWrapper>
          }
        />
        <Route
          path="/order-confirmation-page"
          element={
            <RestaurantSelectionWrapper>
              <OrderConfirmationPage />
            </RestaurantSelectionWrapper>
          }
        />
        <Route
          path="/contact"
          element={
            <RestaurantSelectionWrapper>
              <ContactUsPage />
            </RestaurantSelectionWrapper>
          }
        />
        <Route
          path="/help"
          element={
            <RestaurantSelectionWrapper>
              <HelpFAQPage />
            </RestaurantSelectionWrapper>
          }
        />

        <Route path="*" element={<FallbackRedirect NotFound={NotFoundPage} />} />
      </Routes>
    </Suspense>
  </div>
);

export default ArikasRamNagariInvertedCustomerApp;
